import scopeAndSequence from '@/common/data/scopeAndSequence.json';
import {
  Grapheme,
  GraphemeId,
  Morpheme,
  MorphemeId,
  Phase,
  ScopeAndSequenceLevel,
  Sentence,
  Word,
  WordId,
} from '@/common/types/scopeAndSequence';

// Graphemes
const graphemesById = new Map<string, Grapheme>();
for (const g of scopeAndSequence.graphemes as Grapheme[]) {
  graphemesById.set(g.id, g);
}
const graphemesBySpelling = new Map<string, Grapheme[]>();
for (const g of scopeAndSequence.graphemes as Grapheme[]) {
  const arr = graphemesBySpelling.get(g.spelling) ?? [];
  arr.push(g);
  graphemesBySpelling.set(g.spelling, arr);
}
const graphemesByPronunciation = new Map<string, Grapheme[]>();
for (const g of scopeAndSequence.graphemes as Grapheme[]) {
  const arr = graphemesByPronunciation.get(g.pronunciation) ?? [];
  arr.push(g);
  graphemesByPronunciation.set(g.pronunciation, arr);
}

export function getGraphemeById(id: string): Grapheme | undefined {
  return graphemesById.get(id);
}

export function getGraphemesByIds(ids: string[]): Grapheme[] {
  const graphemes: Grapheme[] = [];
  for (const id of ids) {
    const grapheme = graphemesById.get(id);
    if (grapheme) {
      graphemes.push(grapheme);
    }
  }
  return graphemes;
}

export function getGraphemesBySpelling(spelling: string): Grapheme[] {
  return graphemesBySpelling.get(spelling) ?? [];
}

export function getGraphemesByPronunciation(pronunciation: string): Grapheme[] {
  return graphemesByPronunciation.get(pronunciation) ?? [];
}

/**
 * Returns the phase number of the first phase of the S&S where the grapheme is
 * in the `newGraphemes` of a level in that phase.
 */
export function getFirstPhaseForGrapheme(
  graphemeId: GraphemeId,
): number | null {
  for (const phase of scopeAndSequence.phases) {
    const levelIds = phase.levels;
    const levels = levelIds.map(getLevelById);

    for (const level of levels) {
      if (level?.newGraphemes.includes(graphemeId)) return phase.phase;
    }
  }
  return null; // not found
}

// Morphemes
const morphemesById = new Map<string, Morpheme>();
for (const m of scopeAndSequence.morphemes as Morpheme[]) {
  morphemesById.set(m.id, m);
}
export function getMorphemeById(id: string): Morpheme | undefined {
  return morphemesById.get(id);
}

export function getMorphemesByIds(ids: string[]): Morpheme[] {
  const morphemes: Morpheme[] = [];
  for (const id of ids) {
    const morpheme = morphemesById.get(id);
    if (morpheme) {
      morphemes.push(morpheme);
    }
  }
  return morphemes;
}

// Words
const wordsById = new Map<string, Word>();
for (const w of scopeAndSequence.words as Word[]) {
  wordsById.set(w.id, w);
}

export function getWordById(id: string): Word | undefined {
  return wordsById.get(id);
}

export function getWordsByIds(ids: string[]): Word[] {
  const words: Word[] = [];
  for (const id of ids) {
    const word = wordsById.get(id);
    if (word) {
      words.push(word);
    }
  }
  return words;
}

export function getWordBySpelling(spelling: string): Word | undefined {
  return scopeAndSequence.words.find((w) => w.spelling === spelling) as
    | Word
    | undefined;
}

export function getGraphemeOffsetInWord(
  grapheme: Grapheme,
  word: Word,
): number {
  let offset = 0;
  for (const g of getWordGraphemes(word)) {
    if (g.id === grapheme.id) return offset;
    offset += g.spelling.length;
  }
  return word.spelling.indexOf(grapheme.spelling);
}

export function getWordGraphemes(word: Word): Grapheme[] {
  const wordGraphemes: Grapheme[] = [];
  for (const morphemeId of word.morphemes) {
    const morpheme = morphemesById.get(morphemeId);
    if (!morpheme) continue;
    const morphemeGraphemes = getGraphemesByIds(morpheme.graphemes);
    if (morphemeGraphemes) {
      wordGraphemes.push(...morphemeGraphemes);
    }
  }
  return wordGraphemes;
}

// Sentences
const sentencesById = new Map<string, Sentence>();
for (const s of scopeAndSequence.sentences as Sentence[]) {
  sentencesById.set(s.id, s);
}

export function getSentenceById(id: string): Sentence | undefined {
  return sentencesById.get(id);
}

export function getSentenceBySpelling(spelling: string): Sentence | undefined {
  return scopeAndSequence.sentences.find((s) => s.spelling === spelling) as
    | Sentence
    | undefined;
}

export function getSentencesByIds(ids: string[]): Sentence[] {
  const sentences: Sentence[] = [];
  for (const id of ids) {
    const sentence = sentencesById.get(id);
    if (sentence) {
      sentences.push(sentence);
    }
  }
  return sentences;
}

// Levels
const levelsById = new Map<string, ScopeAndSequenceLevel>();
for (const l of scopeAndSequence.levels as ScopeAndSequenceLevel[]) {
  levelsById.set(l.id, l);
}

export function getLevelById(id: string): ScopeAndSequenceLevel | undefined {
  return levelsById.get(id);
}

export function getLevel(level: number): ScopeAndSequenceLevel | undefined {
  return (scopeAndSequence.levels as ScopeAndSequenceLevel[]).find(
    (l) => l.level === level,
  );
}

// Phases

const phasesById = new Map<string, Phase>();
for (const p of scopeAndSequence.phases as Phase[]) {
  phasesById.set(p.id, p);
}

export function getPhaseById(id: string): Phase | undefined {
  return phasesById.get(id);
}

export function getPhase(phase: number): Phase | undefined {
  return (scopeAndSequence.phases as Phase[]).find((p) => p.phase === phase);
}

// Level lookup helpers
export function getGraphemeLevel(graphemeId: GraphemeId): number {
  const levels = scopeAndSequence.levels as ScopeAndSequenceLevel[];
  for (let i = 1; i < levels.length - 1; i++) {
    if ((levels[i].newGraphemes as string[]).includes(graphemeId)) {
      return levels[i].level;
    }
  }
  return levels[levels.length - 1].level;
}

export function getWordLevel(wordId: WordId): number {
  const levels = scopeAndSequence.levels as ScopeAndSequenceLevel[];
  for (let i = 1; i < levels.length - 1; i++) {
    const levelWords = [
      ...levels[i].wordSet,
      ...levels[i].wordChain,
    ] as string[];
    if (levelWords.includes(wordId)) {
      return levels[i].level;
    }
  }
  return levels[levels.length - 1].level;
}

// Cumulative data
export function getCumulativeGraphemes(level?: number): GraphemeId[] {
  return scopeAndSequence.levels
    .slice(0, level)
    .flatMap((level) => level.newGraphemes as GraphemeId[]);
}

export function getCumulativeMorphemes(
  level?: number,
  affixesOnly?: boolean,
): MorphemeId[] {
  return scopeAndSequence.levels.slice(0, level).flatMap((level) => {
    const levelWordIds = [...level.wordSet, ...level.wordChain] as MorphemeId[];
    const levelWords = getWordsByIds(levelWordIds);

    const levelMorphemes: MorphemeId[] = [];

    for (const word of levelWords) {
      if (affixesOnly) {
        const affixes = getMorphemesByIds(word.morphemes).filter(
          (m) => m.type !== 'base',
        );
        levelMorphemes.push(...affixes.map((a) => a.id));
      } else {
        levelMorphemes.push(...word.morphemes);
      }
    }

    return levelMorphemes;
  });
}

export function getScopeAndSequenceGraphemeFromPronunciation(
  pronunciation: string,
) {
  return scopeAndSequence.graphemes.find(
    (g) => g.pronunciation === pronunciation,
  ) as Grapheme | undefined;
}

// All-items arrays for use in editors/selectors
export const allGraphemes = scopeAndSequence.graphemes as Grapheme[];
export const allMorphemes = scopeAndSequence.morphemes as Morpheme[];
export const allWords = scopeAndSequence.words as Word[];
export const allSentences = scopeAndSequence.sentences as Sentence[];

export function isOneEditDistance(word1: Word, word2: Word): boolean {
  const getWordGraphemeIds = (word: Word): string[] =>
    word.morphemes.flatMap((morphemeId) => {
      const morpheme = getMorphemeById(morphemeId);
      if (!morpheme) return [];
      return morpheme.graphemes;
    });

  const graphemes1 = getWordGraphemeIds(word1);
  const graphemes2 = getWordGraphemeIds(word2);

  if (Math.abs(graphemes1.length - graphemes2.length) > 1) return false;
  if (
    graphemes1.length === graphemes2.length &&
    graphemes1.every((g, i) => g === graphemes2[i])
  )
    return false;

  let i = 0,
    j = 0,
    diff = 0;
  while (i < graphemes1.length && j < graphemes2.length) {
    if (graphemes1[i] === graphemes2[j]) {
      i++;
      j++;
    } else {
      diff++;
      if (diff > 1) return false;
      if (graphemes1.length > graphemes2.length) i++;
      else if (graphemes1.length < graphemes2.length) j++;
      else {
        i++;
        j++;
      }
    }
  }
  if (i < graphemes1.length || j < graphemes2.length) diff++;
  return diff === 1;
}

export function isOneMorphemeEditDistance(word1: Word, word2: Word): boolean {
  const morphemes1 = getMorphemesByIds(word1.morphemes);
  const morphemes2 = getMorphemesByIds(word2.morphemes);

  if (Math.abs(morphemes1.length - morphemes2.length) > 1) return false;
  if (
    morphemes1.length === morphemes2.length &&
    morphemes1.every((m, i) => m === morphemes2[i])
  )
    return false;

  let i = 0,
    j = 0,
    diff = 0;
  while (i < morphemes1.length && j < morphemes2.length) {
    if (morphemes1[i] === morphemes2[j]) {
      i++;
      j++;
    } else {
      diff++;
      if (diff > 1) return false;
      if (morphemes1.length > morphemes2.length) i++;
      else if (morphemes1.length < morphemes2.length) j++;
      else {
        i++;
        j++;
      }
    }
  }
  if (i < morphemes1.length || j < morphemes2.length) diff++;
  return diff === 1;
}

const articulatoryGesturesMap = {
  vowel_1: ['æ'],
  vowel_2: ['ɛ', 'e'],
  vowel_3: ['iː', 'ɪ'],
  vowel_4: ['ə'],
  vowel_5: ['uː'],
  vowel_6: ['ɔː', 'ɔ'],
  vowel_7: ['a', 'ɑː'],
  vowel_8: ['ɒ', 'o'],
  vowel_9: ['ɜː'],
  vowel_10: ['ʊ'],
  vowel_11: ['ʌ'],
  vowel_null: ['∅'],
};
export function getArticulatoryGestures(pronunciation: string) {
  let prevChar: string | null = null;
  const pArr = pronunciation.split('').reduce((acc, char, idx, arr) => {
    if (char === 'ː') return acc;
    if (char === 'ʃ' && prevChar === 't') return acc;
    if (char === 'ʒ' && prevChar === 'd') return acc;

    prevChar = char;

    const nextChar = arr[idx + 1];
    if (nextChar) {
      if (nextChar === 'ː') {
        return [...acc, char + nextChar];
      }

      if (char === 'd' && nextChar === 'ʒ') {
        return [...acc, char + nextChar];
      }

      if (char === 't' && nextChar === 'ʃ') {
        return [...acc, char + nextChar];
      }
    }

    return [...acc, char];
  }, [] as string[]);

  return pArr.map((p) => {
    for (const [key, values] of Object.entries(articulatoryGesturesMap)) {
      if (values.includes(p)) {
        return key;
      }
    }

    // fallback to the original phoneme
    return p;
  });
}
